<template>
  <div class="card detail-card">
    <div class="card-body">
      <div class="detail-grid">
        <div class="detail-section">
          <h6 class="section-title"><i class="fas fa-user mr-2"></i>基本情報</h6>
          <table class="table table-borderless detail-table">
            <tbody>
              <tr><th>名前</th><td>{{ data.name }}</td></tr>
              <tr><th>メールアドレス</th><td>{{ user.email }}</td></tr>
              <tr><th>パスワード</th><td>************</td></tr>
              <tr><th>年齢</th><td>{{ data.age }}歳</td></tr>
              <tr><th>性別</th><td>{{ data.gender }}</td></tr>
              <tr><th>電話番号</th><td>{{ data.phone_number }}</td></tr>
              <tr><th>郵便番号</th><td>{{ data.postal_code }}</td></tr>
              <tr><th>住所</th><td>{{ data.address }}</td></tr>
              <tr><th>入社日</th><td>{{ data.date_of_joining }}</td></tr>
              <tr><th>退職日</th><td>{{ data.date_of_leaving }}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="detail-section">
          <h6 class="section-title"><i class="fas fa-briefcase mr-2"></i>勤務情報</h6>
          <table class="table table-borderless detail-table">
            <tbody>
              <tr><th>勤務時間</th><td>{{ formatTimeShort(data.regular_working_hours_start) }} - {{ formatTimeShort(data.regular_working_hours_stop) }}</td></tr>
              <tr><th>休憩</th><td>{{ data.break_time }}分</td></tr>
              <tr><th>時給</th><td>¥{{ data.hourly_wage ? Number(data.hourly_wage).toLocaleString() : '' }}</td></tr>
              <tr><th>日当交通費</th><td>{{ data.daily_transportation_allowance ? '¥' + Number(data.daily_transportation_allowance).toLocaleString() + '/日' : '' }}</td></tr>
              <tr>
                <th>有給申請システム</th>
                <td>
                  <span :class="data.use_paid_leave ? 'badge-on' : 'badge-off'">{{ data.use_paid_leave ? '利用する' : '利用しない' }}</span>
                </td>
              </tr>
              <tr v-if="data.use_paid_leave">
                <th>有給残日数管理</th>
                <td>
                  <span :class="data.paid_leave ? 'badge-on' : 'badge-off'">{{ data.paid_leave ? '管理する' : '管理しない' }}</span>
                </td>
              </tr>
              <tr v-if="data.use_paid_leave && data.paid_leave"><th>有給残日数</th><td>{{ data.remaining_paid_leave_days }}日</td></tr>
              <tr>
                <th>タイムカード調整</th>
                <td>
                  <span :class="data.time_adjustment ? 'badge-on' : 'badge-off'">{{ data.time_adjustment ? '行う' : '行わない' }}</span>
                </td>
              </tr>
              <tr>
                <th>休日タイプ</th>
                <td>
                  <span class="badge-on" v-if="data.holiday_type === 1">土日祝</span>
                  <span class="badge-shift" v-else-if="data.holiday_type === 2">シフト制（月{{ data.monthly_days_off || 0 }}日休み）</span>
                  <span class="badge-off" v-else>未設定</span>
                </td>
              </tr>
              <tr v-if="data.weekly_cap_hours">
                <th>週上限時間</th>
                <td>{{ data.weekly_cap_hours }}時間</td>
              </tr>
              <tr v-if="data.weekly_target_days_min || data.weekly_target_days_max">
                <th>週稼働日数</th>
                <td>{{ data.weekly_target_days_min || '?' }}〜{{ data.weekly_target_days_max || '?' }}日</td>
              </tr>
              <tr>
                <th>クローズ業務</th>
                <td>
                  <span :class="data.can_close !== false ? 'badge-on' : 'badge-off'">{{ data.can_close !== false ? '可能' : '不可' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Work Patterns -->
      <div class="patterns-section" v-if="data.work_patterns && data.work_patterns.length > 0">
        <h6 class="section-title"><i class="fas fa-clock mr-2"></i>勤務パターン</h6>
        <div class="patterns-grid">
          <div v-for="p in data.work_patterns" :key="p.id" class="pattern-card">
            <div class="pattern-name">{{ p.pattern_name }}</div>
            <div class="pattern-time">{{ p.start_time?.slice(0,5) }} 〜 {{ p.end_time?.slice(0,5) }}</div>
            <div class="pattern-break">休憩 {{ p.break_time }}分</div>
            <div class="pattern-days">
              <span :class="['day-chip', { active: p.monday }]">月</span>
              <span :class="['day-chip', { active: p.tuesday }]">火</span>
              <span :class="['day-chip', { active: p.wednesday }]">水</span>
              <span :class="['day-chip', { active: p.thursday }]">木</span>
              <span :class="['day-chip', { active: p.friday }]">金</span>
              <span :class="['day-chip sat', { active: p.saturday }]">土</span>
              <span :class="['day-chip sun', { active: p.sunday }]">日</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Day Constraints -->
      <div v-if="data.day_constraints && data.day_constraints.length > 0" class="patterns-section">
        <h6 class="section-title"><i class="fas fa-calendar-week mr-2"></i>曜日別勤務制約</h6>
        <div class="day-constraints-grid">
          <div v-for="c in data.day_constraints" :key="c.day_of_week" :class="['dc-card', { 'dc-unavailable': !c.is_available }]">
            <div class="dc-day">{{ dayNames[c.day_of_week] }}</div>
            <div v-if="!c.is_available" class="dc-status">勤務不可</div>
            <div v-else class="dc-time">
              <span v-if="c.available_from && c.available_until">{{ formatTimeShort(c.available_from) }}〜{{ formatTimeShort(c.available_until) }}</span>
              <span v-else-if="c.available_from">{{ formatTimeShort(c.available_from) }}〜</span>
              <span v-else-if="c.available_until">〜{{ formatTimeShort(c.available_until) }}</span>
              <span v-else class="text-muted">制限なし</span>
            </div>
            <div v-if="c.note" class="dc-note">{{ c.note }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer detail-footer">
      <button class="btn btn-outline-secondary" @click="$router.push({name:'StoreStaffPage'})"><i class="fas fa-arrow-left mr-1"></i>一覧に戻る</button>
      <div class="detail-footer-right">
        <button class="btn btn-primary" @click="edit"><i class="fas fa-edit mr-1"></i>編集</button>
        <button class="btn btn-outline-danger" @click="dialog"><i class="fas fa-trash mr-1"></i>削除</button>
      </div>
    </div>
  </div>

  <div class="modal fade" id="modal-default">
    <div class="modal-dialog modal-sm modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center py-4">
          <i class="fas fa-exclamation-triangle text-danger mb-3" style="font-size: 2rem;"></i>
          <p class="mb-1"><strong>{{ data.name }}</strong>を削除します。</p>
          <p class="text-muted mb-0">この操作は取り消せません。</p>
        </div>
        <div class="modal-footer justify-content-center">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">キャンセル</button>
          <button type="button" class="btn btn-danger" @click="remove">削除する</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showModal, hideModal } from '../../utils/bsModal'
export default {
  data() {
    return {
      data: [],
      user: [],
      dayNames: ['日', '月', '火', '水', '木', '金', '土'],
    }
  },
  mounted() {
    this.getStaff();
  },
  methods: {
    formatTimeShort(t) {
      return t ? t.slice(0, 5) : '';
    },
    dialog() {
      showModal('modal-default');
    },
    dialogClose() {
      hideModal('modal-default');
    },
    edit() {
      this.$router.push({name:'StoreStaffEditPage', params: {staff_id: this.$route.params.staff_id}});
    },
    getStaff () {
      const staff_id = this.$route.params.staff_id;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/staff/'+staff_id)
      .then(response => {
        if (response.data.success) {
          this.data = response.data.data;
          this.user = this.data.user
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data.message);
        }
      });
    },
    remove () {
      const staff_id = this.$route.params.staff_id;
      this.$http.delete(process.env.VUE_APP_API_PATH + '/shop/staff/'+staff_id)
      .then(response => {
        if (response.data.success) {
          this.dialogClose();
          this.$toast.success(response.data.message);
          setTimeout(() => {
            this.$router.push({name:'CompanyStaffPage'});
          }, 1000);
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data.message);
        }
      });
    }
  },
};
</script>

<style scoped>
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #049C4C;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: 10px;
  margin-bottom: 0;
  border-bottom: 2px solid #e2e8f0;
}

.detail-table th {
  width: 40%;
  font-weight: 600;
  font-size: 13px;
  color: #718096;
  padding: 10px 8px;
  border: none;
  white-space: nowrap;
}

.detail-table td {
  font-size: 15px;
  color: #1a202c;
  font-weight: 500;
  padding: 10px 8px;
  border: none;
}

.badge-on {
  display: inline-block;
  background: #c6f6d5;
  color: #22543d;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}

.badge-off {
  display: inline-block;
  background: #e2e8f0;
  color: #718096;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}

.badge-shift {
  display: inline-block;
  background: #bee3f8;
  color: #2a4365;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}

.patterns-section {
  margin-top: 32px;
}

.patterns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.pattern-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
  background: #f7fafc;
}

.pattern-name {
  font-weight: 700;
  font-size: 15px;
  color: #1a202c;
  margin-bottom: 6px;
}

.pattern-time {
  font-size: 16px;
  font-weight: 600;
  color: #049C4C;
  margin-bottom: 2px;
}

.pattern-break {
  font-size: 13px;
  color: #718096;
  margin-bottom: 8px;
}

.pattern-days {
  display: flex;
  gap: 4px;
}

.day-chip {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: #e2e8f0;
  color: #a0aec0;
}

.day-chip.active {
  background: #049C4C;
  color: #fff;
}

.day-chip.sat.active {
  background: #3182ce;
}

.day-chip.sun.active {
  background: #e53e3e;
}

/* Day constraints */
.day-constraints-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-top: 12px;
}

@media (max-width: 768px) {
  .day-constraints-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.dc-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 8px;
  text-align: center;
  background: #f0fff4;
}

.dc-unavailable {
  background: #fff5f5;
  border-color: #fed7d7;
}

.dc-day {
  font-size: 14px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.dc-status {
  font-size: 11px;
  font-weight: 600;
  color: #e53e3e;
}

.dc-time {
  font-size: 12px;
  font-weight: 600;
  color: #049C4C;
}

.dc-note {
  font-size: 10px;
  color: #718096;
  margin-top: 4px;
  line-height: 1.3;
}

.detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.detail-footer-right {
  display: flex;
  gap: 12px;
}
</style>
